"use client";

import { useAuth } from "@clerk/clerk-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listWatermarkTemplates,
  createWatermarkTemplate,
  updateWatermarkTemplate,
  deleteWatermarkTemplate,
  type WatermarkTemplate,
  type CreateWatermarkTemplateRequest,
  type UpdateWatermarkTemplateRequest,
} from "@/api/template.service";

export const WATERMARK_TEMPLATES_QUERY_KEY = ["watermark-templates"];

/** Fetch all watermark templates for the current user */
export function useWatermarkTemplates() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<{ templates: WatermarkTemplate[] }>({
    queryKey: WATERMARK_TEMPLATES_QUERY_KEY,
    queryFn: () => listWatermarkTemplates(getToken),
    enabled: isSignedIn,
    staleTime: 1000 * 60 * 5,
  });
}

/** Create a new watermark template */
export function useCreateWatermarkTemplate() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateWatermarkTemplateRequest) =>
      createWatermarkTemplate(body, getToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATERMARK_TEMPLATES_QUERY_KEY });
    },
  });
}

/** Update an existing watermark template by ID */
export function useUpdateWatermarkTemplate() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateWatermarkTemplateRequest) =>
      updateWatermarkTemplate(id, body, getToken),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(WATERMARK_TEMPLATES_QUERY_KEY, (old: any) => {
        if (!old || !old.templates) return old;
        return {
          ...old,
          templates: old.templates.map((t: any) => 
            t.id === variables.id 
              ? { ...t, ...variables }
              : t
          )
        };
      });
      queryClient.invalidateQueries({ queryKey: WATERMARK_TEMPLATES_QUERY_KEY });
    },
  });
}

/** Delete a watermark template by ID */
export function useDeleteWatermarkTemplate() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteWatermarkTemplate(id, getToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATERMARK_TEMPLATES_QUERY_KEY });
    },
  });
}
